import { createStore } from "@/src/shared/utils/createStore";
import { wishlistApi } from "../api/queries";
import {
  WISHLIST_HYDRATE_PAGE_SIZE,
  wishlistService,
  type TSavedIn,
} from "../services";
import type { IWishlist, IWishlistCreateRequest } from "../types";

/** Which sheet is open, and for which listing. */
export type TWishlistSheet =
  | { type: "save"; listingId: number; moveFrom?: number }
  /** `fromSave`: opened from the save sheet, so it can go back to it. */
  | { type: "create"; listingId?: number; fromSave?: boolean }
  | null;

/**
 * The web port of the app's `WishlistSaveService`: one store for every heart on the
 * page, so a listing shows the same state on its card, its details page and its sheet.
 */
export const wishlistStore = createStore({
  folders: [] as IWishlist[],
  /** listing id → folders it is saved in. */
  savedIn: {} as TSavedIn,
  /** Listings an API marked `is_favourite` before the folders were loaded. */
  favourites: [] as number[],
  /** Listings with a request in flight: their heart spins and ignores taps. */
  pending: [] as number[],
  hydrated: false,
  /** The user the state belongs to; another user's login starts from scratch. */
  ownerId: null as number | null,
  sheet: null as TWishlistSheet,
});

let hydration: Promise<void> | null = null;

const setPending = (listingId: number, isPending: boolean) =>
  wishlistStore.set(({ pending }) => ({
    pending: isPending
      ? [...pending, listingId]
      : pending.filter((id) => id !== listingId),
  }));

const bumpCount = (folderId: number, delta: number) =>
  wishlistStore.set(({ folders }) => ({
    folders: folders.map((folder) =>
      folder.id === folderId
        ? {
            ...folder,
            total_count: Math.max(0, (folder.total_count ?? 0) + delta),
          }
        : folder,
    ),
  }));

/** Runs `action` with the listing marked pending; always clears it. */
const withPending = async <T>(
  listingId: number,
  action: () => Promise<T>,
): Promise<T> => {
  setPending(listingId, true);
  try {
    return await action();
  } finally {
    setPending(listingId, false);
  }
};

export const wishlistActions = {
  /**
   * Learns every saved listing once per session: the folders, then each folder's
   * listings in parallel (50 per call, as the app). A failure is silent; the next
   * call retries.
   */
  hydrate(force = false): Promise<void> {
    if (wishlistStore.get().hydrated && !force) return Promise.resolve();
    hydration ??= (async () => {
      try {
        const { data: folders } = await wishlistApi.getWishlists(
          1,
          WISHLIST_HYDRATE_PAGE_SIZE,
        );
        const items = await Promise.all(
          folders.map(async (folder) => ({
            folderId: folder.id,
            listingIds: (
              await wishlistApi.getItems(
                folder.id,
                1,
                WISHLIST_HYDRATE_PAGE_SIZE,
              )
            ).data.listing.map((listing) => listing.id),
          })),
        );
        wishlistStore.set({
          folders,
          savedIn: wishlistService.savedInFrom(items),
          favourites: [],
          hydrated: true,
        });
      } catch {
        // Stay unhydrated: the next heart tap tries again.
      } finally {
        hydration = null;
      }
    })();
    return hydration;
  },

  /** Takes an API's `is_favourite`: true fills the heart, false clears it everywhere. */
  markFavourite(listingId: number, isFavourite: boolean) {
    wishlistStore.set(({ favourites, savedIn }) =>
      isFavourite
        ? {
            favourites: favourites.includes(listingId)
              ? favourites
              : [...favourites, listingId],
          }
        : {
            favourites: favourites.filter((id) => id !== listingId),
            savedIn: wishlistService.withRemoved(savedIn, listingId),
          },
    );
  },

  /** Makes the store belong to `userId`, dropping another user's state first. */
  claim(userId: number) {
    if (wishlistStore.get().ownerId === userId) return;
    wishlistActions.reset();
    wishlistStore.set({ ownerId: userId });
  },

  /** Forgets everything; the next heart reloads it (login, logout). */
  reset() {
    hydration = null;
    wishlistStore.reset();
  },

  openSheet(sheet: TWishlistSheet) {
    wishlistStore.set({ sheet });
  },

  closeSheet() {
    wishlistStore.set({ sheet: null });
  },

  async save(listingId: number, folderId: number): Promise<void> {
    await withPending(listingId, () =>
      wishlistApi.addListing(folderId, listingId),
    );
    wishlistStore.set(({ savedIn }) => ({
      savedIn: wishlistService.withSaved(savedIn, listingId, folderId),
    }));
    bumpCount(folderId, 1);
  },

  /** Removes the listing from one folder, or from every folder it is in. */
  async remove(listingId: number, folderId?: number): Promise<void> {
    const { savedIn } = wishlistStore.get();
    const folderIds =
      folderId === undefined ? (savedIn[listingId] ?? []) : [folderId];
    await withPending(listingId, () =>
      Promise.all(
        folderIds.map((id) => wishlistApi.removeListing(id, listingId)),
      ),
    );
    wishlistStore.set(({ savedIn: current, favourites }) => ({
      savedIn:
        folderId === undefined
          ? wishlistService.withRemoved(current, listingId)
          : wishlistService.withRemoved(current, listingId, folderId),
      favourites:
        folderId === undefined
          ? favourites.filter((id) => id !== listingId)
          : favourites,
    }));
    folderIds.forEach((id) => bumpCount(id, -1));
  },

  /** Adds to the new folder first, then removes from the old one, as the app. */
  async move(listingId: number, from: number, to: number): Promise<void> {
    await wishlistActions.save(listingId, to);
    await wishlistActions.remove(listingId, from);
  },

  /** Creates a folder (and saves `listing_id` into it in the same call). */
  async create(request: IWishlistCreateRequest): Promise<IWishlist> {
    const created = await (request.listing_id === undefined
      ? wishlistApi.createWishlist(request)
      : withPending(request.listing_id, () =>
          wishlistApi.createWishlist(request),
        ));
    const folder: IWishlist = {
      ...created,
      total_count: created.total_count ?? created.listing.length,
    };
    wishlistStore.set(({ folders, savedIn }) => {
      const others = folders.filter((existing) => existing.id !== folder.id);
      const next = [folder, ...others];
      return {
        folders: folder.is_default
          ? wishlistService.withDefault(next, folder.id)
          : next,
        savedIn:
          request.listing_id === undefined
            ? savedIn
            : wishlistService.withSaved(savedIn, request.listing_id, folder.id),
      };
    });
    return folder;
  },

  /** Deletes a folder and forgets the listings it held. */
  async deleteFolder(folderId: number): Promise<void> {
    await wishlistApi.deleteWishlist(folderId);
    wishlistStore.set(({ folders, savedIn }) => ({
      folders: folders.filter((folder) => folder.id !== folderId),
      savedIn: Object.fromEntries(
        Object.entries(savedIn)
          .map(([listingId, ids]) => [
            listingId,
            ids.filter((id) => id !== folderId),
          ])
          .filter(([, ids]) => (ids as number[]).length > 0),
      ) as TSavedIn,
    }));
  },

  /** Keeps a renamed folder's name in step with the server. */
  renamed(folderId: number, name: string) {
    wishlistStore.set(({ folders }) => ({
      folders: folders.map((folder) =>
        folder.id === folderId ? { ...folder, name } : folder,
      ),
    }));
  },
};
